import type {
  ApiResponse,
  AuthResponse,
  AvailabilitySearchRequest,
  AvailabilitySearchResponse,
  BookingConfirmation,
  BookingIdentityRequest,
  BookingIntentRequest,
  BookingIntentResponse,
  BookingPaymentSession,
  Faq,
  FaqInput,
  FeaturedRoomsResponse,
  GalleryItem,
  GalleryItemInput,
  MemberBookingsResponse,
  MemberPasswordChange,
  MemberProfile,
  MemberProfileUpdate,
  MemberRegisterRequest,
  Membership,
  PublicUser,
  RefreshResponse,
  SecretsEvent,
  SecretsGalleryImage,
  SecretsNewsArticle,
  Slide,
  SlideInput,
} from '@secretsoverflow/types';

/** An admin-panel account. `PublicUser` narrowed to the staff roles. */
export interface StaffUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: 'admin' | 'editor';
  createdAt: string;
  updatedAt: string;
}

export const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, string[]>;

  constructor(status: number, code: string, message: string, details?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** In-memory access token (never persisted to storage). */
let accessToken: string | null = null;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};
export const getAccessToken = (): string | null => accessToken;

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Send the httpOnly refresh cookie (auth endpoints). */
  auth?: boolean;
  /** Attach the bearer access token. */
  bearer?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.bearer && accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${API_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    credentials: options.auth ? 'include' : 'same-origin',
    signal: options.signal,
  });

  const payload = (await res.json().catch(() => null)) as ApiResponse<T> | null;

  if (!payload) {
    throw new ApiClientError(res.status, 'network_error', 'Empty or invalid response');
  }
  if (!payload.ok) {
    throw new ApiClientError(
      res.status,
      payload.error.code,
      payload.error.message,
      payload.error.details,
    );
  }
  return payload.data;
}

/** Build the availability query string (origin is always Secrets Hideaway). */
function availabilityQuery(params: AvailabilitySearchRequest): string {
  return new URLSearchParams({
    radiusKm: String(params.radiusKm),
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    guests: String(params.guests),
  }).toString();
}

/** POST a file as the raw request body (the avatar endpoint takes one image). */
async function requestRaw<T>(path: string, file: File): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': file.type };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers,
    body: file,
    credentials: 'same-origin',
  });

  const payload = (await res.json().catch(() => null)) as ApiResponse<T> | null;
  if (!payload) throw new ApiClientError(res.status, 'network_error', 'Empty or invalid response');
  if (!payload.ok) {
    throw new ApiClientError(
      res.status,
      payload.error.code,
      payload.error.message,
      payload.error.details,
    );
  }
  return payload.data;
}

export const api = {
  availability: {
    search: (params: AvailabilitySearchRequest, signal?: AbortSignal) =>
      request<AvailabilitySearchResponse>(`/v1/availability?${availabilityQuery(params)}`, {
        signal,
      }),
    // The nearby-hotel showcase, cached server-side on a slow cadence.
    featured: (signal?: AbortSignal) =>
      request<FeaturedRoomsResponse>('/v1/availability/featured', { signal }),
  },
  // A booking is recorded when a room is picked, before anyone has identified
  // themselves, so an abandoned checkout still leaves us the search behind it.
  // Both calls send the bearer token when there is one, which is what links the
  // record to a signed-in member.
  bookings: {
    start: (body: BookingIntentRequest) =>
      request<BookingIntentResponse>('/v1/bookings', { method: 'POST', body, bearer: true }),
    identify: (id: string, body: BookingIdentityRequest) =>
      request<{ recorded: boolean }>(`/v1/bookings/${id}/identity`, {
        method: 'POST',
        body,
        bearer: true,
      }),
    // Holds the room and returns what the vendor's payment SDK needs. The
    // client secret is used immediately and never stored.
    payment: (id: string) =>
      request<BookingPaymentSession>(`/v1/bookings/${id}/payment`, {
        method: 'POST',
        bearer: true,
      }),
    // Places the reservation. Idempotent server-side, so a double submit or a
    // reload returns the existing confirmation rather than booking twice.
    confirm: (id: string) =>
      request<BookingConfirmation>(`/v1/bookings/${id}/confirm`, {
        method: 'POST',
        bearer: true,
      }),
  },
  // Members authenticate against the Secrets API via our backend, which then
  // issues one of our sessions. Refresh and logout are shared with `auth`.
  member: {
    login: (body: { email: string; password: string }) =>
      request<{ member: MemberProfile; accessToken: string }>('/v1/member/login', {
        method: 'POST',
        body,
        auth: true,
      }),
    register: (body: MemberRegisterRequest) =>
      request<{ member: MemberProfile; accessToken: string | null; pendingReason: string | null }>(
        '/v1/member/register',
        { method: 'POST', body, auth: true },
      ),
    me: () => request<MemberProfile>('/v1/member/me', { bearer: true }),
    bookings: (signal?: AbortSignal) =>
      request<MemberBookingsResponse>('/v1/member/me/bookings', { bearer: true, signal }),
    update: (body: MemberProfileUpdate) =>
      request<MemberProfile>('/v1/member/me', { method: 'PUT', body, bearer: true }),
    changePassword: (body: MemberPasswordChange) =>
      request<{ changed: boolean }>('/v1/member/me/password', {
        method: 'PUT',
        body,
        bearer: true,
      }),
    uploadAvatar: (file: File) => requestRaw<MemberProfile>('/v1/member/me/avatar', file),
    // Both public: somebody who has forgotten their password has no session.
    requestPasswordReset: (email: string) =>
      request<{ sent: boolean }>('/v1/member/password-reset', {
        method: 'POST',
        body: { email },
        auth: true,
      }),
    confirmPasswordReset: (token: string, password: string) =>
      request<{ reset: boolean }>('/v1/member/password-reset/confirm', {
        method: 'POST',
        body: { token, password },
        auth: true,
      }),
  },
  auth: {
    register: (body: { email: string; password: string; firstName?: string; lastName?: string }) =>
      request<AuthResponse>('/v1/auth/register', { method: 'POST', body, auth: true }),
    login: (body: { email: string; password: string }) =>
      request<AuthResponse>('/v1/auth/login', { method: 'POST', body, auth: true }),
    refresh: () => request<RefreshResponse>('/v1/auth/refresh', { method: 'POST', auth: true }),
    logout: () => request<{ loggedOut: boolean }>('/v1/auth/logout', { method: 'POST', auth: true }),
    me: () => request<PublicUser>('/v1/auth/me', { bearer: true }),
  },
  membership: {
    me: () => request<Membership>('/v1/membership/me', { bearer: true }),
  },
  admin: {
    // Admin-panel accounts. Separate from `members`, which are shadow rows for
    // secretsfl.com guests whose password lives in the Secrets API.
    staff: {
      list: () => request<{ items: StaffUser[] }>('/v1/admin/users', { bearer: true }),
      create: (body: {
        email: string;
        password: string;
        role: 'admin' | 'editor';
        firstName?: string;
        lastName?: string;
      }) => request<StaffUser>('/v1/admin/users', { method: 'POST', body, bearer: true }),
      update: (id: string, body: { role?: 'admin' | 'editor'; password?: string }) =>
        request<StaffUser>(`/v1/admin/users/${id}`, { method: 'PATCH', body, bearer: true }),
      remove: (id: string) =>
        request<{ deleted: boolean }>(`/v1/admin/users/${id}`, { method: 'DELETE', bearer: true }),
    },
    overview: () =>
      request<{
        members: { total: number; admins: number; members: number };
        memberships: { total: number; active: number };
        bookings: {
          total: number;
          confirmed: number;
          started: number;
          /** The vendor refused the book. What became of any payment is unknown. */
          failed: number;
          revenueCents: number;
          guests: number;
          members: number;
          withIdentity: number;
        };
        bookingsByDay: Array<{ day: string; started: number; confirmed: number }>;
        /**
         * Everything that reached the vendor, for cross-checking against Nuitee.
         *
         * OPTIONAL on purpose, and it must stay that way. Web and API deploy
         * independently and the web bundle lands first by several minutes, so
         * between the two every newly-added field is genuinely absent from the
         * responses the new page receives. Typing it as required let a `.length`
         * through that took the whole admin dashboard down in production.
         */
        vendorBookings?: Array<{
          id: string;
          status: string;
          guestEmail: string | null;
          guestName: string;
          hotelName: string;
          checkIn: string;
          totalPriceCents: number;
          currency: string;
          prebookedAt: string | null;
          settledAt: string | null;
          vendorBookingId: string | null;
          vendorConfirmationCode: string | null;
          vendorPrebookId: string | null;
          vendorTransactionId: string | null;
          vendorPaymentIntentId: string | null;
        }>;
        topHotels: Array<{
          hotelName: string;
          city: string | null;
          bookings: number;
          confirmed: number;
          avgNightlyCents: number;
        }>;
        stayDemand: Array<{ date: string; searched: number; booked: number; confirmed: number }>;
        cache: { total: number; fresh: number; stale: number; provider: string };
        searchesByDay: Array<{ day: string; count: number }>;
      }>('/v1/admin/overview', { bearer: true }),
    /**
     * One booking as we hold it, beside the vendor's own record, with the
     * disagreements named. A live vendor call, so it is fetched per row on
     * demand and never for the table.
     */
    reconcileBooking: (id: string) =>
      request<{
        ours: {
          id: string;
          status: string;
          guestName: string;
          guestEmail: string | null;
          hotelName: string;
          checkIn: string;
          checkOut: string;
          totalPriceCents: number;
          currency: string;
          vendorBookingId: string | null;
          vendorConfirmationCode: string | null;
          vendorPrebookId: string | null;
          vendorTransactionId: string | null;
          vendorPaymentIntentId: string | null;
          prebookedAt: string | null;
          paidAt: string | null;
          confirmedAt: string | null;
          createdAt: string | null;
          updatedAt: string | null;
        };
        /** Null when the vendor holds no such booking — which is normal for a failed row. */
        vendor: {
          vendorBookingId: string;
          status: string;
          confirmationCode: string | null;
          totalPriceCents: number | null;
          currency: string | null;
          clientReference?: string | null;
          commissionCents?: number | null;
          paymentStatus?: string | null;
          paymentTransactionId?: string | null;
          prebookId?: string | null;
          checkIn?: string | null;
          checkOut?: string | null;
          hotelId?: string | null;
          hotelName?: string | null;
          createdAt?: string | null;
          cancelledAt?: string | null;
          refundedAt?: string | null;
          refundedCents?: number | null;
          refundable?: boolean | null;
          freeCancellationUntil?: string | null;
          sandbox?: boolean | null;
        } | null;
        lookup: 'vendor_id' | 'client_reference' | 'never_sent' | 'unsupported';
        provider: string;
        environment: 'sandbox' | 'live';
        drift: Array<{
          code: string;
          severity: 'critical' | 'warning' | 'info';
          label: string;
          ours: string | null;
          theirs: string | null;
        }>;
        /** Set when the vendor could not be reached: unknown, not "no booking". */
        vendorError: string | null;
        checkedAt: string;
      }>(`/v1/admin/bookings/${id}/reconcile`, { bearer: true }),
    /**
     * What we have actually earned, from the vendor's own per-booking
     * commission. Grouped by currency throughout, so amounts settled in
     * different currencies are never added together.
     *
     * `coverage` is not decoration: it says how many confirmed bookings the
     * totals could not see, and a total read without it is a guess.
     */
    earnings: () =>
      request<{
        totals: Array<{
          currency: string;
          bookings: number;
          commissionCents: number;
          roomRevenueCents: number;
          payableCommissionCents: number;
          upcomingCommissionCents: number;
        }>;
        byMonth: Array<{
          key: string;
          label: string;
          currency: string;
          bookings: number;
          commissionCents: number;
          roomRevenueCents: number;
        }>;
        byHotel: Array<{
          key: string;
          label: string;
          currency: string;
          bookings: number;
          commissionCents: number;
          roomRevenueCents: number;
        }>;
        coverage: {
          confirmed: number;
          synced: number;
          missing: number;
          lastSyncedAt: string | null;
        };
      }>('/v1/admin/earnings', { bearer: true }),
    /**
     * The six curated properties as the vendor holds them, with a live
     * cheapest-rate probe. A hotel with no rate for the stay is the signal
     * worth having: it has quietly stopped appearing on the site.
     */
    hotels: (params: { checkIn?: string; checkOut?: string; guests?: number; refresh?: boolean } = {}) => {
      const query = new URLSearchParams();
      if (params.checkIn) query.set('checkIn', params.checkIn);
      if (params.checkOut) query.set('checkOut', params.checkOut);
      if (params.guests) query.set('guests', String(params.guests));
      if (params.refresh) query.set('refresh', 'true');
      const suffix = query.toString() ? `?${query.toString()}` : '';
      return request<{
        stay: { checkIn: string; checkOut: string; guests: number };
        currency: string;
        provider: string;
        hotels: Array<{
          id: string;
          detail: {
            id: string;
            name: string;
            description: string | null;
            address: string | null;
            city: string | null;
            zip: string | null;
            starRating: number | null;
            guestRating: number | null;
            reviewCount: number | null;
            checkInFrom: string | null;
            checkOutBy: string | null;
            phone: string | null;
            email: string | null;
            images: string[];
            facilities: string[];
            petsAllowed: boolean | null;
            childrenAllowed: boolean | null;
          } | null;
          reviews: Array<{
            score: number | null;
            country: string | null;
            date: string | null;
            headline: string | null;
            pros: string | null;
            cons: string | null;
          }>;
          rate: {
            hotelId: string;
            sellingPriceCents: number | null;
            netPriceCents: number | null;
            offerId: string | null;
          } | null;
          status: 'selling' | 'no_rate' | 'unknown_to_vendor';
          error: string | null;
        }>;
        /** Set when the probe itself failed: prices unknown, not absent. */
        rateError: string | null;
        checkedAt: string;
      }>(`/v1/admin/hotels${suffix}`, { bearer: true });
    },
    /** Refresh the vendor snapshots now. Admin-only; costs one vendor call per booking. */
    syncVendorBookings: (limit = 25) =>
      request<{ checked: number; updated: number; failed: number; remaining: number }>(
        `/v1/admin/bookings/sync-vendor?limit=${limit}`,
        { method: 'POST', bearer: true },
      ),
    members: (page = 1, pageSize = 25) =>
      request<{ items: PublicUser[]; total: number; page: number; pageSize: number }>(
        `/v1/admin/members?page=${page}&pageSize=${pageSize}`,
        { bearer: true },
      ),
    availabilityCache: () =>
      request<{
        total: number;
        fresh: number;
        stale: number;
        provider: string;
        recent: Array<{ queryText: string; expiresAt: string; createdAt: string }>;
      }>('/v1/admin/availability-cache', { bearer: true }),
  },
  // Public content reads for the template pages. Slides + FAQs are admin-managed;
  // Gallery, News, and Events come from the SecretsFL API.
  content: {
    slides: () => request<Slide[]>('/v1/content/slides'),
    faqs: () => request<Faq[]>('/v1/content/faqs'),
    gallery: () => request<SecretsGalleryImage[]>('/v1/content/gallery'),
    news: () => request<SecretsNewsArticle[]>('/v1/content/news'),
    newsItem: (id: string) => request<SecretsNewsArticle>(`/v1/content/news/${id}`),
    events: () => request<SecretsEvent[]>('/v1/content/events'),
    eventItem: (id: string) => request<SecretsEvent>(`/v1/content/events/${id}`),
  },
  // Admin CRUD for the managed content collections.
  adminContent: {
    slides: {
      list: () => request<Slide[]>('/v1/admin/content/slides', { bearer: true }),
      create: (body: SlideInput) =>
        request<Slide>('/v1/admin/content/slides', { method: 'POST', body, bearer: true }),
      update: (id: string, body: SlideInput) =>
        request<Slide>(`/v1/admin/content/slides/${id}`, { method: 'PUT', body, bearer: true }),
      remove: (id: string) =>
        request<{ deleted: boolean }>(`/v1/admin/content/slides/${id}`, {
          method: 'DELETE',
          bearer: true,
        }),
    },
    gallery: {
      list: () => request<GalleryItem[]>('/v1/admin/content/gallery', { bearer: true }),
      create: (body: GalleryItemInput) =>
        request<GalleryItem>('/v1/admin/content/gallery', { method: 'POST', body, bearer: true }),
      update: (id: string, body: GalleryItemInput) =>
        request<GalleryItem>(`/v1/admin/content/gallery/${id}`, {
          method: 'PUT',
          body,
          bearer: true,
        }),
      remove: (id: string) =>
        request<{ deleted: boolean }>(`/v1/admin/content/gallery/${id}`, {
          method: 'DELETE',
          bearer: true,
        }),
    },
    faqs: {
      list: () => request<Faq[]>('/v1/admin/content/faqs', { bearer: true }),
      create: (body: FaqInput) =>
        request<Faq>('/v1/admin/content/faqs', { method: 'POST', body, bearer: true }),
      update: (id: string, body: FaqInput) =>
        request<Faq>(`/v1/admin/content/faqs/${id}`, { method: 'PUT', body, bearer: true }),
      remove: (id: string) =>
        request<{ deleted: boolean }>(`/v1/admin/content/faqs/${id}`, {
          method: 'DELETE',
          bearer: true,
        }),
    },
  },
};
